import {
  ORIGINAL_VALUE_FIELD,
  TRANSLATION_OBJECT_FIELD,
} from '@config/global-constants';
import {
  get,
  has,
  isEmpty,
  isPlainObject,
  omit,
  reduce,
  replace,
} from 'lodash';

/**
 * Use this to map an object to i18n subject with optional prefix/suffix for each key
 *
 * @param obj
 * @param options
 * @returns
 */
export const mapToI18nSubj = <
  T extends { [TRANSLATION_OBJECT_FIELD]?: Object } = {
    [TRANSLATION_OBJECT_FIELD]?: Object;
  },
  R extends T = any,
>(
  obj: T,
  options: {
    prefix?: string;
    suffix?: string;
    split?: string;
    /**
     * whether to remove prefix/suffix instead of adding them
     */
    isRemoveMode?: boolean;
  },
): R => {
  const { split = '_', prefix, suffix, isRemoveMode = false } = options || {};

  const addedPrefix = isEmpty(prefix) ? '' : `${prefix}${split}`;
  const addedSuffix = isEmpty(suffix) ? '' : `${split}${suffix}`;

  const mappedValue = reduce(
    omit(obj as Object, TRANSLATION_OBJECT_FIELD),
    (newValue, value, key) => {
      const newKey = isRemoveMode
        ? replace(key, new RegExp(`^${addedPrefix}|${addedSuffix}$`, 'g'), '')
        : `${addedPrefix}${key}${addedSuffix}`;

      if (isPlainObject(value)) {
        return {
          ...newValue,
          [newKey]: mapToI18nSubj(value as unknown as T, options),
        };
      }

      return { ...newValue, [newKey]: value };
    },
    {},
  );

  if (has(obj, TRANSLATION_OBJECT_FIELD)) {
    const i18nData = get(obj, TRANSLATION_OBJECT_FIELD);
    // map i18n separately to add prefix/suffix and remove old i18n key
    const i18nOriginalValue = mapToI18nSubj(
      get(obj, [TRANSLATION_OBJECT_FIELD, ORIGINAL_VALUE_FIELD]),
      options,
    );

    const i18nValue = reduce(
      omit(i18nData, ORIGINAL_VALUE_FIELD),
      (newI18nValue, value, key) => {
        return {
          ...newI18nValue,
          [key]: mapToI18nSubj(value, options),
        };
      },
      {},
    );

    return {
      ...mappedValue,
      [TRANSLATION_OBJECT_FIELD]: {
        ...i18nValue,
        original: i18nOriginalValue,
      },
    } as unknown as R;
  }

  return mappedValue as unknown as R;
};
